// Flagged Picks — Pick Face walk for Assistant Team Leaders and above
// (per Alex, 2026-10-08). Pembroke only.
//
// Items a picker could not find at the pick face but which are in stock up
// high become tasks (BE services/flagged-picks.js). A leader scans a tote,
// is walked to each high location in walk order, scans every unit into the
// tote, and finally shelves the items in the packing room one at a time:
// scan a unit out of the tote, scan the staging shelf it goes on (per
// Alex, 2026-10-13 — spread over the racking, the packer spots theirs at a
// glance). The walk finishes by itself when the tote is empty. Nothing
// moves through inventory here — the units are allocated to their orders
// from the moment the task exists — so the walk is pure guidance:
// tote → location → items → … → unit → shelf → unit → shelf.
//
// Shape follows Put Away's Consolidate walk (one step on screen at a time,
// the scanner decides the step, buttons only for the choices a scan can't
// express: Next location, Skip, Not here, Leave walk).
//
// Second pill: Put back — tasks cancelled AFTER the unit was picked are
// still on the staging shelf. One unit at a time (per Alex, 2026-10-13):
// scan a unit off the shelf (or tap its row), scan it again for each
// further unit going to the same place, then scan the racking location.
// A task's units can so be restocked to different locations; the task
// closes when its last shelved unit has gone back.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Box, Check, Minus, PackageSearch, PackageX, Plus, Undo2, User } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { usePageTitle } from "../hooks/usePageTitle";
import HiddenScanInput from "../components/HiddenScanInput";
import FitWord from "../components/FitWord";
import { apiFetch } from "../api/apiFetch";
import { playSuccess, playError, playBoxAdd, playToteFinalised } from "../components/helpers/audioPlayer";
import { preventScanPageButtonFocus, blurClickedScanPageButton } from "../components/helpers/scanFocusGuard";
import { useReadOnly } from "../access/ReadOnly";
import type { FlaggedPickTask, FlaggedPickStop } from "../types/Dispatch";
import "../styles/scansheet.css";
import "../styles/flagged-picks.css";

type Pill = "walk" | "put_back";
type Step = "tote" | "location" | "items" | "stage";
type Session = { sessionId: number; toteId: string };
// The put-back item in hand and how many of its units the next location
// scan puts back (one per unit scan, or the +/- on the row).
type PutBackPick = { taskId: number; qty: number };

// Units of a put-back task still on the staging shelf.
const putBackLeft = (task: FlaggedPickTask) =>
    Math.max(0, (task.qtyPicked > 0 ? task.qtyPicked : task.qty) - (task.qtyPutBack ?? 0));
type Confirm =
    | { kind: "not_here"; task: FlaggedPickTask }
    | { kind: "abandon" };

const getUser = (): { uid: number | null; name: string; warehouse: string } => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return { uid: null, name: "", warehouse: "" };
        const u = JSON.parse(raw);
        return { uid: Number(u?.uid) || null, name: u?.name ?? "", warehouse: u?.warehouse ?? "" };
    } catch { return { uid: null, name: "", warehouse: "" }; }
};

const TOAST = {
    server: "fp-server",
    tote: "fp-tote",
    location: "fp-location",
    scan: "fp-scan",
    stage: "fp-stage",
    info: "fp-info",
};

const FlaggedPicks = () => {
    usePageTitle("flagged_picks");
    const { t } = useTranslation();
    const navigate = useNavigate();
    const readOnly = useReadOnly();
    const user = getUser();

    const [pill, setPill] = useState<Pill>("walk");
    const [step, setStep] = useState<Step>("tote");
    const [session, setSession] = useState<Session | null>(null);
    const [target, setTarget] = useState<FlaggedPickStop | null>(null);
    const [remainingUnits, setRemainingUnits] = useState(0);
    const [pickedUnits, setPickedUnits] = useState(0);
    // The stage step: what is in the tote (picked, not shelved), what this
    // walk has shelved so far, and the unit in hand waiting for its shelf.
    const [tote, setTote] = useState<FlaggedPickTask[]>([]);
    const [shelved, setShelved] = useState<FlaggedPickTask[]>([]);
    const [stagePick, setStagePick] = useState<number | null>(null);
    const [summary, setSummary] = useState<{ openUnits: number; openTasks: number; awaitingUnits: number; walker: { userName: string; toteId: string } | null; available: boolean }>({
        openUnits: 0, openTasks: 0, awaitingUnits: 0, walker: null, available: true,
    });
    const [putBack, setPutBack] = useState<FlaggedPickTask[]>([]);
    const [putBackPick, setPutBackPick] = useState<PutBackPick | null>(null);
    const [confirm, setConfirm] = useState<Confirm | null>(null);
    const [busy, setBusy] = useState(false);
    const [scanValue, setScanValue] = useState("");
    const skippedRef = useRef<string[]>([]);
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    // ── BE helpers ──────────────────────────────────────────────────
    const post = useCallback(async (path: string, body: Record<string, unknown>) => {
        try {
            const res = await apiFetch(path, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => null);
            if (!res.ok || !data) {
                playError();
                toast.error(data?.message || t("server_error"), { toastId: TOAST.server });
                return null;
            }
            return data;
        } catch {
            playError();
            toast.error(t("server_error"), { toastId: TOAST.server });
            return null;
        }
    }, [t]);

    const applyWalk = useCallback((data: { sessionId: number; toteId: string; stops?: FlaggedPickStop[]; tote?: FlaggedPickTask[]; shelved?: FlaggedPickTask[]; remainingUnits?: number; pickedUnits?: number }) => {
        setSession({ sessionId: Number(data.sessionId), toteId: String(data.toteId) });
        const stops = Array.isArray(data.stops) ? data.stops : [];
        setRemainingUnits(Number(data.remainingUnits ?? 0));
        setPickedUnits(Number(data.pickedUnits ?? 0));
        setTote(Array.isArray(data.tote) ? data.tote : []);
        setShelved(Array.isArray(data.shelved) ? data.shelved : []);
        setStagePick(null);
        if (stops.length > 0) { setTarget(stops[0]); setStep("location"); }
        else { setTarget(null); setStep("stage"); }
    }, []);

    const resetWalk = useCallback(() => {
        setSession(null);
        setTarget(null);
        setStep("tote");
        setRemainingUnits(0);
        setPickedUnits(0);
        setTote([]);
        setShelved([]);
        setStagePick(null);
        skippedRef.current = [];
    }, []);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams({ warehouse: user.warehouse });
            const [listRes, activeRes] = await Promise.all([
                apiFetch(`/flagged-picks/list?${params}`, {}),
                apiFetch(`/flagged-picks/active`, {}),
            ]);
            if (!mountedRef.current) return;
            if (listRes.ok) {
                const data = await listRes.json();
                const tasks: FlaggedPickTask[] = Array.isArray(data.tasks) ? data.tasks : [];
                const open = tasks.filter(x => x.status === "pending" || x.status === "picking");
                setSummary({
                    openUnits: open.reduce((n, x) => n + (x.qty - x.qtyPicked), 0),
                    openTasks: open.length,
                    // Units whose only stock is in a drop tote: not on the
                    // walk until the put-away lands.
                    awaitingUnits: tasks.filter(x => x.status === "awaiting_stock").reduce((n, x) => n + x.qty, 0),
                    walker: data.walk && data.walk.userName ? { userName: data.walk.userName, toteId: data.walk.toteId } : null,
                    available: data.available !== false,
                });
                setPutBack(tasks.filter(x => x.status === "put_back"));
            }
            if (activeRes.ok) {
                const data = await activeRes.json();
                if (data?.valid === false && data.reason === "not_available") {
                    setSummary(s => ({ ...s, available: false }));
                } else if (data?.active?.sessionId) {
                    applyWalk(data.active);
                }
            }
        } catch {
            if (mountedRef.current) toast.error(t("server_error"), { toastId: TOAST.server });
        }
    }, [applyWalk, t, user.warehouse]);

    useEffect(() => { load(); }, [load]);

    // The item in hand follows the list: gone (finished elsewhere) → drop
    // it; fewer units left than counted → clamp.
    useEffect(() => {
        setPutBackPick(prev => {
            if (!prev) return prev;
            const task = putBack.find(x => x.id === prev.taskId);
            const left = task ? putBackLeft(task) : 0;
            if (left <= 0) return null;
            return prev.qty > left ? { taskId: prev.taskId, qty: left } : prev;
        });
    }, [putBack]);

    // ── Walk transitions ────────────────────────────────────────────
    const advanceFrom = useCallback(async (location: string) => {
        if (!session) return;
        const data = await post(`/flagged-picks/next`, { sessionId: session.sessionId, locationId: location, exclude: skippedRef.current });
        if (!data) return;
        if (data.valid === false) {
            if (data.reason === "not_active") { toast.info(t("fp_session_ended"), { toastId: TOAST.info }); resetWalk(); await load(); }
            return;
        }
        if (data.done) {
            // Nothing left on the walk and nothing in the tote (every unit
            // written off): the BE closed it and freed the tote.
            toast.info(t("fp_walk_closed_empty"), { toastId: TOAST.info });
            resetWalk();
            await load();
            return;
        }
        if (data.skippedOnly) skippedRef.current = [];
        setRemainingUnits(Number(data.remainingUnits ?? 0));
        setPickedUnits(Number(data.pickedUnits ?? 0));
        if (Array.isArray(data.tote)) setTote(data.tote);
        if (data.next) { setTarget(data.next); setStep("location"); }
        else { setTarget(null); setStep("stage"); }
    }, [load, post, resetWalk, session, t]);

    const openTote = async (val: string) => {
        const data = await post(`/flagged-picks/open-tote`, { toteId: val });
        if (!data) return;
        if (!data.valid) {
            playError();
            const msg = data.reason === "not_available" ? t("fp_not_available")
                : data.reason === "no_tasks" ? t("fp_no_tasks")
                : data.reason === "locked" ? t("fp_locked", { by: data.openedBy ?? "" })
                : data.reason === "user_has_open_tote" ? t("fp_user_has_open_tote", { tote: data.openToteId ?? "" })
                : t("fp_tote_rejected", { reason: String(data.reason ?? "") });
            toast.error(msg, { toastId: TOAST.tote });
            return;
        }
        skippedRef.current = [];
        applyWalk(data);
        playBoxAdd();
    };

    const arrive = async (val: string) => {
        if (!session || !target) return;
        if (val !== target.location) {
            // A staging shelf scanned mid-walk is "I'm done": go and shelve
            // what is in the tote; the rest is handed back once it is empty.
            const probe = await post(`/flagged-picks/open-location`, { sessionId: session.sessionId, location: val });
            if (probe && probe.valid === false && probe.reason === "staging") {
                if (tote.length === 0) { playError(); toast.info(t("fp_stage_tote_empty"), { toastId: TOAST.location }); return; }
                setStagePick(null);
                setStep("stage");
                playBoxAdd();
                return;
            }
            if (probe && probe.valid === false && probe.reason === "not_active") { toast.info(t("fp_session_ended"), { toastId: TOAST.info }); resetWalk(); await load(); return; }
            playError();
            toast.error(t("fp_wrong_location", { location: target.display }), { toastId: TOAST.location });
            return;
        }
        const data = await post(`/flagged-picks/open-location`, { sessionId: session.sessionId, location: val });
        if (!data) return;
        if (data.valid === false) {
            if (data.reason === "not_active") { toast.info(t("fp_session_ended"), { toastId: TOAST.info }); resetWalk(); await load(); return; }
            playError();
            toast.error(data.reason === "not_a_stop" ? t("fp_not_a_stop") : t("invalid_location"), { toastId: TOAST.location });
            return;
        }
        setTarget(prev => prev ? { ...prev, tasks: data.tasks } : prev);
        setStep("items");
        playBoxAdd();
    };

    const scanUnit = async (val: string) => {
        if (!session || !target) return;
        const data = await post(`/flagged-picks/scan`, { sessionId: session.sessionId, location: target.location, barcode: val });
        if (!data) return;
        if (data.valid === false && data.reason === "not_active") { toast.info(t("fp_session_ended"), { toastId: TOAST.info }); resetWalk(); await load(); return; }
        if (data.outcome !== "matched") {
            playError();
            const msg = data.outcome === "unknown" ? t("fp_scan_unknown")
                : data.outcome === "done" ? t("fp_scan_done")
                : data.outcome === "not_a_stop" ? t("fp_not_a_stop")
                : t("fp_scan_not_wanted");
            toast.error(msg, { toastId: TOAST.scan });
            return;
        }
        setTarget(prev => prev ? { ...prev, tasks: data.tasks } : prev);
        setRemainingUnits(n => Math.max(0, n - 1));
        setPickedUnits(n => n + 1);
        // The tote list follows: the scanned task is in it, one unit more.
        const scanned = (data.tasks as FlaggedPickTask[] | undefined)?.find(x => x.id === Number(data.taskId));
        if (scanned) setTote(prev => prev.some(x => x.id === scanned.id) ? prev.map(x => x.id === scanned.id ? scanned : x) : [...prev, scanned]);
        playSuccess();
        if (data.locationDone) await advanceFrom(target.location);
    };

    // ── Stage (one item at a time) ──────────────────────────────────
    const stageTask = stagePick === null ? null : tote.find(x => x.id === stagePick) ?? null;

    // The unit in hand goes on the shelf `val`. Returns false when `val` is
    // not a location at all (so the scan can be tried as a unit instead).
    const stageItemAt = async (val: string): Promise<boolean> => {
        if (!session || !stageTask) return false;
        const data = await post(`/flagged-picks/stage`, { sessionId: session.sessionId, location: val, taskId: stageTask.id });
        if (!data) return true;
        if (data.valid === false) {
            if (data.reason === "unknown_location") return false;
            if (data.reason === "not_active") { toast.info(t("fp_session_ended"), { toastId: TOAST.info }); resetWalk(); await load(); return true; }
            playError();
            if (data.reason === "not_in_tote") { toast.error(t("fp_stage_not_in_tote"), { toastId: TOAST.stage }); setStagePick(null); return true; }
            toast.error(data.reason === "not_staging" ? t("fp_not_staging") : t("invalid_location"), { toastId: TOAST.stage });
            return true;
        }
        const display = String(data.display ?? val);
        if (data.done) {
            playToteFinalised();
            toast.success(t("fp_stage_done", { units: String(Number(data.stagedUnits ?? 0) + shelved.reduce((n, x) => n + x.qtyPicked, 0)) }), { toastId: TOAST.stage });
            if (Number(data.returned ?? 0) > 0) {
                toast.info(t("fp_stage_returned", { count: String(data.returned) }), { toastId: TOAST.info });
            }
            resetWalk();
            await load();
            return true;
        }
        playSuccess();
        toast.success(t("fp_stage_item_done", { units: String(data.stagedUnits ?? stageTask.qtyPicked), sku: stageTask.sku, location: display }), { toastId: TOAST.stage });
        setTote(Array.isArray(data.tote) ? data.tote : tote.filter(x => x.id !== stageTask.id));
        setShelved(Array.isArray(data.shelved) ? data.shelved : [...shelved, { ...stageTask, status: "staged", stagingLocation: val, stagingDisplay: display }]);
        setStagePick(null);
        return true;
    };

    const stageScan = async (val: string) => {
        if (!session) return;
        // A unit the tote list already identifies (sku / product barcode / asin).
        const local = tote.find(x => x.sku === val || x.barcode === val || x.asin === val);
        if (local) { setStagePick(local.id); playBoxAdd(); return; }
        // With a unit in hand the scan is most likely its shelf.
        if (stageTask && await stageItemAt(val)) return;
        // Otherwise a unit whose barcode the list does not carry.
        const data = await post(`/flagged-picks/tote/scan`, { sessionId: session.sessionId, barcode: val });
        if (!data) return;
        if (data.valid === false && data.reason === "not_active") { toast.info(t("fp_session_ended"), { toastId: TOAST.info }); resetWalk(); await load(); return; }
        if (data.valid === false || !data.task) {
            playError();
            const msg = data.reason === "not_in_tote" ? t("fp_stage_not_in_tote")
                : stageTask ? t("invalid_location")
                : t("fp_stage_scan_unknown");
            toast.error(msg, { toastId: TOAST.stage });
            return;
        }
        const hit = data.task as FlaggedPickTask;
        if (!tote.some(x => x.id === hit.id)) setTote(prev => [...prev, hit]);
        setStagePick(hit.id);
        playBoxAdd();
    };

    // Back from the stage step to the stops still to walk.
    const continueWalk = () => {
        if (!target || busy) return;
        setStagePick(null);
        setStep("location");
    };

    // ── Put back (one unit at a time) ───────────────────────────────
    const putBackTask = putBackPick ? putBack.find(x => x.id === putBackPick.taskId) ?? null : null;

    // A unit scanned (or a row tapped): a new item starts at one unit; the
    // same item again counts one more, up to what is on the shelf.
    const pickPutBackItem = (task: FlaggedPickTask) => {
        const left = putBackLeft(task);
        if (left <= 0) return;
        if (putBackPick && putBackPick.taskId === task.id) {
            if (putBackPick.qty >= left) {
                playError();
                toast.info(t("fp_put_back_all_counted", { count: String(left) }), { toastId: TOAST.info });
                return;
            }
            setPutBackPick({ taskId: task.id, qty: putBackPick.qty + 1 });
        } else {
            setPutBackPick({ taskId: task.id, qty: 1 });
        }
        playBoxAdd();
    };

    const stepPutBackQty = (delta: number) => {
        if (!putBackPick || !putBackTask) return;
        const next = Math.min(putBackLeft(putBackTask), Math.max(1, putBackPick.qty + delta));
        if (next !== putBackPick.qty) setPutBackPick({ taskId: putBackPick.taskId, qty: next });
    };

    // The counted units go back to racking at `val`. Returns false when
    // `val` is not a location at all (so the scan can be tried as a unit).
    const putBackAt = async (val: string): Promise<boolean> => {
        if (!putBackPick || !putBackTask) return false;
        const data = await post(`/flagged-picks/put-back/done`, { taskId: putBackTask.id, location: val, qty: putBackPick.qty });
        if (!data) return true;
        if (data.valid === false) {
            if (data.reason === "unknown_location") return false;
            playError();
            if (data.reason === "not_put_back" || data.reason === "nothing_left") {
                toast.error(t("fp_put_back_gone"), { toastId: TOAST.location });
                setPutBackPick(null);
                await load();
                return true;
            }
            toast.error(data.reason === "not_storage" ? t("fp_put_back_not_storage") : t("invalid_location"), { toastId: TOAST.location });
            return true;
        }
        playSuccess();
        toast.success(t("fp_put_back_done", { units: String(data.units ?? putBackPick.qty), location: String(data.display ?? val) }), { toastId: TOAST.info });
        // More of the same item still on the shelf: keep it in hand at one
        // unit, so the next location scan puts the next one back.
        setPutBackPick(data.done || Number(data.remaining ?? 0) <= 0 ? null : { taskId: putBackTask.id, qty: 1 });
        await load();
        return true;
    };

    const putBackScan = async (val: string) => {
        // A unit the list already identifies (sku / product barcode / asin).
        const local = putBack.find(x => putBackLeft(x) > 0 && (x.sku === val || x.barcode === val || x.asin === val));
        if (local) { pickPutBackItem(local); return; }
        // With an item in hand the scan is most likely the racking location.
        if (putBackTask && await putBackAt(val)) return;
        // Otherwise a unit whose barcode the list does not carry.
        const data = await post(`/flagged-picks/put-back/scan`, { barcode: val });
        if (!data) return;
        if (data.valid === false || !data.task) {
            playError();
            const msg = data.reason === "not_on_shelf" ? t("fp_put_back_not_on_shelf")
                : putBackTask ? t("invalid_location")
                : t("fp_put_back_scan_unknown");
            toast.error(msg, { toastId: TOAST.scan });
            return;
        }
        const hit = data.task as FlaggedPickTask;
        if (!putBack.some(x => x.id === hit.id)) setPutBack(prev => [...prev, hit]);
        pickPutBackItem(putBack.find(x => x.id === hit.id) ?? hit);
    };

    // ── Scanner ─────────────────────────────────────────────────────
    const doSubmit = async (scannedValue?: string) => {
        const val = (scannedValue ?? scanValue).trim();
        setScanValue("");
        if (!val || busy || confirm) return;
        if (readOnly) { playError(); toast.error(t("read_only_mode"), { toastId: TOAST.info }); return; }
        setBusy(true);
        try {
            if (pill === "put_back") { await putBackScan(val); return; }
            if (step === "tote") { await openTote(val); return; }
            if (session && val === session.toteId) return;
            if (step === "location") { await arrive(val); return; }
            if (step === "items") {
                if (target && val === target.location) return;
                await scanUnit(val);
                return;
            }
            if (step === "stage") { await stageScan(val); return; }
        } finally {
            if (mountedRef.current) setBusy(false);
        }
    };

    // ── Buttons ─────────────────────────────────────────────────────
    const onNext = async () => {
        if (!target || busy) return;
        setBusy(true);
        try { await advanceFrom(target.location); }
        finally { if (mountedRef.current) setBusy(false); }
    };
    const onSkip = async () => {
        if (!target || busy) return;
        setBusy(true);
        try {
            skippedRef.current = [...skippedRef.current, target.location];
            await advanceFrom(target.location);
        } finally { if (mountedRef.current) setBusy(false); }
    };
    const onConfirm = async () => {
        if (!confirm || busy) return;
        setBusy(true);
        try {
            if (confirm.kind === "not_here" && session && target) {
                const data = await post(`/flagged-picks/not-here`, { sessionId: session.sessionId, taskId: confirm.task.id });
                setConfirm(null);
                if (!data) return;
                if (data.valid === false) { toast.info(t("fp_session_ended"), { toastId: TOAST.info }); resetWalk(); await load(); return; }
                if (data.done) {
                    // That was the last unit on the walk and the tote is
                    // empty: the BE has closed the walk and freed the tote.
                    toast.info(t("fp_walk_closed_empty"), { toastId: TOAST.info });
                    resetWalk();
                    await load();
                    return;
                }
                setTarget(prev => prev ? { ...prev, tasks: data.tasks } : prev);
                if (typeof data.remainingUnits === "number") setRemainingUnits(data.remainingUnits);
                else setRemainingUnits(n => Math.max(0, n - (confirm.task.qty - confirm.task.qtyPicked)));
                // The BE searched again: stock found elsewhere is already on
                // this walk; stock in a drop tote waits; the rest is short.
                const found = Number(data.found ?? 0);
                const awaiting = Number(data.awaiting ?? 0);
                const msg = found > 0
                    ? t("fp_not_here_found", { units: String(found), locations: (data.newStops ?? []).join(", ") })
                    : awaiting > 0 ? t("fp_not_here_awaiting", { units: String(awaiting) })
                    : t("fp_not_here_done");
                toast.info(msg, { toastId: TOAST.info });
                if (data.locationDone) await advanceFrom(target.location);
            } else if (confirm.kind === "abandon" && session) {
                const data = await post(`/flagged-picks/abandon`, { sessionId: session.sessionId });
                setConfirm(null);
                if (!data) return;
                if (data.valid === false && data.reason === "must_stage") { playError(); toast.error(t("fp_abandon_must_stage"), { toastId: TOAST.info }); return; }
                resetWalk();
                await load();
            }
        } finally { if (mountedRef.current) setBusy(false); }
    };

    // ── Render ──────────────────────────────────────────────────────
    const commandWords = pill === "put_back"
        ? (putBackTask ? [t("scan_location")] : [t("scan_item")])
        : step === "tote" ? [t("scan_tote")]
        : step === "location" ? [t("put_away_high_go_to")]
        : step === "items" ? [t("scan_item")]
        : stageTask ? [t("scan_location")]
        : [t("scan_item")];
    const subText = pill === "put_back"
        ? (putBackTask ? t("fp_put_back_scan", { qty: String(putBackPick?.qty ?? 1), sku: putBackTask.sku }) : t("fp_put_back_hint"))
        : step === "tote" ? t("fp_sub_tote")
        : step === "location" ? t("fp_sub_go_to")
        : step === "items" ? t("fp_sub_items")
        : stageTask ? t("fp_stage_scan_for", { sku: stageTask.sku })
        : t("fp_sub_stage");

    const locationDone = !!target && target.tasks.every(x => x.qtyPicked >= x.qty);
    const walkerIsOther = summary.walker && summary.walker.userName !== user.name;

    const renderTask = (task: FlaggedPickTask, extra?: React.ReactNode) => {
        const done = task.qtyPicked >= task.qty;
        return (
            <div key={task.id} className={`fp-task${done ? " fp-task--done" : ""}`} data-testid="fp-task" data-sku={task.sku}>
                {task.thumbnailUrl || task.imageUrl
                    ? <img className="fp-task-img" src={task.thumbnailUrl ?? task.imageUrl ?? ""} alt="" />
                    : <div className="fp-task-img fp-task-img--empty"><PackageSearch size={22} /></div>}
                <div className="fp-task-main">
                    <span className="fp-task-title">{task.title}</span>
                    <div className="fp-task-ids">
                        <span>{task.sku}</span>
                        {task.asin && task.asin !== task.sku && <span>{task.asin}</span>}
                        {task.barcode && task.barcode !== task.sku && task.barcode !== task.asin && <span>{task.barcode}</span>}
                    </div>
                    <span className="fp-task-order">{t("fp_for_order", { order: task.orderNumber })}</span>
                </div>
                <div className="fp-task-side">
                    <span className="fp-task-count">{done ? <Check size={22} /> : `${task.qtyPicked} / ${task.qty}`}</span>
                    {extra}
                </div>
            </div>
        );
    };

    return (
        <div className="scan-panel fp-panel" onMouseDown={preventScanPageButtonFocus} onClick={blurClickedScanPageButton}>
            <div className="page-title-bar">
                <h1 className="page-title">{t("flagged_picks")}</h1>
                {!session && (
                    <div className="fp-pills" role="tablist">
                        <button type="button" role="tab" aria-selected={pill === "walk"} className={`fp-pill${pill === "walk" ? " fp-pill--active" : ""}`} onClick={() => { setPill("walk"); setPutBackPick(null); }}>
                            {t("fp_pill_walk")}
                        </button>
                        <button type="button" role="tab" aria-selected={pill === "put_back"} className={`fp-pill${pill === "put_back" ? " fp-pill--active" : ""}`} onClick={() => setPill("put_back")} data-testid="fp-pill-put-back">
                            {t("fp_pill_put_back")}{putBack.length > 0 ? ` (${putBack.length})` : ""}
                        </button>
                    </div>
                )}
                <button className="page-back-btn" onClick={() => navigate("/pick-face")} aria-label={t("back")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            <div className="scan-centre">
                <div className="scan-command-wrap">
                    <div className="scan-command">
                        {commandWords.map((w, i) => <FitWord key={i}>{w}</FitWord>)}
                    </div>
                    <p className="scan-command-sub">{subText}</p>
                </div>
            </div>

            {pill === "walk" && !summary.available && (
                <p className="fp-landing-hint" style={{ textAlign: "center" }}>{t("fp_not_available")}</p>
            )}

            {pill === "walk" && step === "tote" && summary.available && (
                <div className="fp-landing" data-testid="fp-landing">
                    <span className={`fp-landing-count${summary.openUnits === 0 ? " fp-landing-count--zero" : ""}`} data-testid="fp-landing-count">
                        {summary.openUnits}
                    </span>
                    <p className="fp-landing-hint">
                        {summary.openUnits === 0 ? t("fp_landing_none") : t("fp_landing_open", { count: String(summary.openUnits) })}
                    </p>
                    {summary.openUnits > 0 && <p className="fp-landing-hint">{t("fp_landing_hint")}</p>}
                    {summary.awaitingUnits > 0 && (
                        <p className="fp-landing-hint fp-landing-awaiting" data-testid="fp-landing-awaiting">
                            {t("fp_landing_awaiting", { count: String(summary.awaitingUnits) })}
                        </p>
                    )}
                    {walkerIsOther && summary.walker && (
                        <span className="fp-landing-walker"><User size={14} />{t("fp_walk_in_progress", { name: summary.walker.userName, tote: summary.walker.toteId })}</span>
                    )}
                </div>
            )}

            {pill === "walk" && session && (
                <div className="fp-tote-bar" data-testid="fp-tote-bar">
                    <span className="fp-tote-bar-left"><Box size={16} />{t("fp_tote", { tote: session.toteId })}</span>
                    <span className="fp-tote-bar-right">{t("fp_remaining_units", { count: String(remainingUnits) })}</span>
                </div>
            )}

            {pill === "walk" && target && (step === "location" || step === "items") && (
                <div className="fp-card" data-testid="fp-card" data-step={step}>
                    <div className="fp-card-location">{target.display}</div>
                    {step === "location" && (
                        <p className="fp-card-sub">
                            {target.tasks.map(x => x.title).join(" · ")}
                        </p>
                    )}
                    {step === "items" && (
                        <div className="fp-task-list">
                            {target.tasks.map(task => renderTask(task, task.qtyPicked < task.qty ? (
                                <button type="button" className="fp-task-not-here" onClick={() => setConfirm({ kind: "not_here", task })} disabled={busy || readOnly} data-testid="fp-not-here">
                                    <PackageX size={14} />{t("fp_not_here_btn")}
                                </button>
                            ) : undefined))}
                        </div>
                    )}
                </div>
            )}

            {pill === "walk" && target && step === "items" && (
                <div className="fp-actions">
                    <button type="button" className={`btn ${locationDone ? "btn-success" : "btn-secondary"}`} onClick={onNext} disabled={busy} data-testid="fp-next">
                        {t("fp_next_location")}
                    </button>
                </div>
            )}
            {pill === "walk" && target && step === "location" && (
                <div className="fp-actions">
                    <button type="button" className="btn btn-secondary" onClick={onSkip} disabled={busy} data-testid="fp-skip">
                        {t("fp_skip_location")}
                    </button>
                    {pickedUnits === 0 && (
                        <button type="button" className="btn btn-ghost" onClick={() => setConfirm({ kind: "abandon" })} disabled={busy} data-testid="fp-abandon">
                            {t("fp_abandon")}
                        </button>
                    )}
                </div>
            )}
            {pill === "walk" && step === "stage" && session && (
                <div className="fp-card fp-stage" data-testid="fp-stage">
                    <div className="fp-stage-title">{t("fp_stage_tote_title")}</div>
                    <div className="fp-task-list" data-testid="fp-stage-tote">
                        {tote.map(task => {
                            const selected = stageTask?.id === task.id;
                            return (
                                <div
                                    key={task.id}
                                    className={`fp-task fp-putback-row${selected ? " fp-putback-row--selected" : ""}`}
                                    onClick={() => { if (!busy) { setStagePick(selected ? null : task.id); } }}
                                    role="button"
                                    tabIndex={0}
                                    data-testid="fp-stage-row"
                                    data-selected={selected ? "true" : "false"}
                                >
                                    {task.thumbnailUrl || task.imageUrl
                                        ? <img className="fp-task-img" src={task.thumbnailUrl ?? task.imageUrl ?? ""} alt="" />
                                        : <div className="fp-task-img fp-task-img--empty"><PackageSearch size={22} /></div>}
                                    <div className="fp-task-main">
                                        <span className="fp-task-title">{task.title}</span>
                                        <div className="fp-task-ids">
                                            <span>{task.sku}</span>
                                            {task.asin && task.asin !== task.sku && <span>{task.asin}</span>}
                                        </div>
                                        <span className="fp-task-order">{t("fp_for_order", { order: task.orderNumber })}</span>
                                    </div>
                                    <div className="fp-task-side"><span className="fp-task-count">x{task.qtyPicked}</span></div>
                                </div>
                            );
                        })}
                    </div>
                    {shelved.length > 0 && (
                        <>
                            <div className="fp-stage-title fp-stage-title--shelved">{t("fp_stage_shelved_title")}</div>
                            <div className="fp-task-list" data-testid="fp-stage-shelved">
                                {shelved.map(task => (
                                    <div key={task.id} className="fp-task fp-task--done" data-testid="fp-stage-shelved-row">
                                        {task.thumbnailUrl || task.imageUrl
                                            ? <img className="fp-task-img" src={task.thumbnailUrl ?? task.imageUrl ?? ""} alt="" />
                                            : <div className="fp-task-img fp-task-img--empty"><Check size={22} /></div>}
                                        <div className="fp-task-main">
                                            <span className="fp-task-title">{task.title}</span>
                                            <div className="fp-task-ids"><span>{task.sku}</span></div>
                                            <span className="fp-putback-shelf">{t("fp_stage_shelved_at", { location: task.stagingDisplay ?? task.stagingLocation ?? "" })}</span>
                                        </div>
                                        <div className="fp-task-side"><span className="fp-task-count">x{task.qtyPicked}</span></div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
            {pill === "walk" && step === "stage" && session && target && remainingUnits > 0 && (
                <div className="fp-actions">
                    <button type="button" className="btn btn-ghost" onClick={continueWalk} disabled={busy} data-testid="fp-stage-continue">
                        {t("fp_stage_continue")}
                    </button>
                </div>
            )}
            {pill === "walk" && step === "stage" && session && tote.length === 0 && (
                // Nothing to shelve: there is no shelf scan that would end the
                // walk, so the way out is Leave walk (which the BE accepts
                // because nothing is picked). Without this the tote stayed
                // open and the leader was locked out of every tote screen.
                <div className="fp-actions fp-actions--stacked" data-testid="fp-stage-empty">
                    <p className="fp-landing-hint" style={{ textAlign: "center" }}>
                        {target ? t("fp_stage_tote_empty") : t("fp_stage_nothing_left")}
                    </p>
                    <button type="button" className="btn btn-secondary" onClick={() => setConfirm({ kind: "abandon" })} disabled={busy} data-testid="fp-abandon">
                        {t("fp_abandon")}
                    </button>
                </div>
            )}

            {pill === "put_back" && (
                <div className="fp-putback-list" data-testid="fp-put-back-list">
                    {putBack.length === 0
                        ? <p className="fp-landing-hint" style={{ textAlign: "center" }}>{t("fp_put_back_none")}</p>
                        : putBack.map(task => {
                            const left = putBackLeft(task);
                            const selected = putBackTask?.id === task.id;
                            const qty = selected ? (putBackPick?.qty ?? 1) : 0;
                            return (
                                <div
                                    key={task.id}
                                    className={`fp-task fp-putback-row${selected ? " fp-putback-row--selected" : ""}`}
                                    onClick={() => { if (!selected && !busy) pickPutBackItem(task); }}
                                    role="button"
                                    tabIndex={0}
                                    data-testid="fp-put-back-row"
                                    data-selected={selected ? "true" : "false"}
                                >
                                    {task.thumbnailUrl || task.imageUrl
                                        ? <img className="fp-task-img" src={task.thumbnailUrl ?? task.imageUrl ?? ""} alt="" />
                                        : <div className="fp-task-img fp-task-img--empty"><Undo2 size={22} /></div>}
                                    <div className="fp-task-main">
                                        <span className="fp-task-title">{task.title}</span>
                                        <div className="fp-task-ids"><span>{task.sku}</span>{task.asin && <span>{task.asin}</span>}</div>
                                        <span className="fp-putback-shelf">{t("fp_status_put_back", { location: task.stagingDisplay ?? task.stagingLocation ?? "" })}</span>
                                    </div>
                                    <div className="fp-task-side"><span className="fp-task-count" data-testid="fp-put-back-left">x{left}</span></div>
                                    {selected && (
                                        <div className="fp-putback-qty" onClick={e => e.stopPropagation()} data-testid="fp-put-back-qty">
                                            <span className="fp-putback-qty-label">{t("fp_put_back_qty_label")}</span>
                                            <span className="fp-putback-stepper">
                                                <button type="button" className="fp-putback-step" onClick={() => stepPutBackQty(-1)} disabled={busy || qty <= 1} aria-label="-" data-testid="fp-put-back-minus"><Minus size={16} /></button>
                                                <span className="fp-putback-qty-value" data-testid="fp-put-back-qty-value">{qty} <small>/ {left}</small></span>
                                                <button type="button" className="fp-putback-step" onClick={() => stepPutBackQty(1)} disabled={busy || qty >= left} aria-label="+" data-testid="fp-put-back-plus"><Plus size={16} /></button>
                                            </span>
                                            <button type="button" className="btn btn-ghost" onClick={() => setPutBackPick(null)} disabled={busy} data-testid="fp-put-back-clear">
                                                {t("fp_put_back_clear")}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                </div>
            )}

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={busy || confirm !== null}
            />

            {confirm && (
                <div className="fp-confirm" role="dialog" aria-modal="true" data-testid="fp-confirm">
                    <div className="fp-confirm-card">
                        <p>
                            {confirm.kind === "not_here"
                                ? t("fp_not_here_confirm", { location: target?.display ?? "" })
                                : t("fp_abandon_confirm")}
                        </p>
                        <div className="fp-confirm-actions">
                            <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={busy} data-testid="fp-confirm-yes">{t("confirm")}</button>
                            <button type="button" className="btn btn-ghost" onClick={() => setConfirm(null)} disabled={busy}>{t("cancel")}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FlaggedPicks;
